/* eslint-disable react/prop-types */
import React, { useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listGridPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import EventContent from './EventContent';
import MobileCalendarToolbar from './MobileCalendarToolbar';
import { returnStatusColor } from './returnStatusColor';
import useMediaQuery from '../../hooks/useMediaQuery';

const Calendar = ({ posts, onEventClick }) => {
  const calendarRef = useRef(null);
  const { isMobile, isSmallMobile } = useMediaQuery('(max-width: 768px)');
  const isMobileView = isMobile || isSmallMobile;

  // Add Joyride selector class to view change button group
  useEffect(() => {
    const buttonGroup = document.querySelector('.fc-header-toolbar .fc-button-group');
    if (buttonGroup) {
      buttonGroup.classList.add('desktop-tour-calendar-change-view');
    }
  }, []);

  // Scroll to first event (or current time) when view changes
  const handleDatesSet = (dateInfo) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;

    const viewType = dateInfo.view.type;
    // Only scroll in time-grid views
    if (!viewType.startsWith('timeGrid')) return;

    const rangeStart = dateInfo.start;
    const rangeEnd = dateInfo.end;

    // Find the earliest scheduled post within the visible range
    const inRange = posts
      .map((p) => new Date(p.scheduledAt))
      .filter((d) => d >= rangeStart && d < rangeEnd)
      .sort((a, b) => a - b);

    let scrollTarget;
    if (inRange.length > 0) {
      // 30 min before first event, floored to the hour
      const first = inRange[0];
      const minutes = first.getHours() * 60 + first.getMinutes() - 30;
      const clamped = Math.max(0, minutes);
      const h = String(Math.floor(clamped / 60)).padStart(2, '0');
      const m = String(clamped % 60).padStart(2, '0');
      scrollTarget = `${h}:${m}:00`;
    } else {
      // Fall back to current time minus 30 min
      const now = new Date();
      const minutes = Math.max(0, now.getHours() * 60 + now.getMinutes() - 30);
      const h = String(Math.floor(minutes / 60)).padStart(2, '0');
      const m = String(minutes % 60).padStart(2, '0');
      scrollTarget = `${h}:${m}:00`;
    }

    // Use setTimeout to let FullCalendar finish rendering before scrolling
    setTimeout(() => {
      api.scrollToTime(scrollTarget);
    }, 50);
  };

  // Handle next/prev 3 days on mobile
  const handleNext = () => {
    if (isMobile && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;

      if (view.type === 'timeGrid') {
        calendarApi.next();
      } else {
        calendarApi.next();
      }
    } else {
      calendarRef.current?.getApi().next();
    }
  };

  const handlePrev = () => {
    if (isMobile && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;

      if (view.type === 'timeGrid') {
        calendarApi.prev();
      } else {
        calendarApi.prev();
      }
    } else {
      calendarRef.current?.getApi().prev();
    }
  };

  // Transform posts into FullCalendar events
  const events = posts.map((post) => {
    const { backgroundColor, borderColor } = returnStatusColor(post.status);

    return {
      id: post.id,
      title: post.aspectRatio === '9:16' ? 'Story' : 'Feed',
      start: post.scheduledAt,
      extendedProps: {
        post,
        postId: post.id,
        status: post.status,
        aspectRatio: post.aspectRatio,
        imageUrl: post.path || post.imageUrl,
        caption: post.caption,
      },
      backgroundColor,
      borderColor,
    };
  });

  return (
    <div className={`h-full flex flex-col `}>
      {isMobileView && (
        <MobileCalendarToolbar onPrev={handlePrev} onNext={handleNext} calendarRef={calendarRef} />
      )}
      <div className='flex-1 pt-4 md:pt-0'>
        <FullCalendar
          ref={calendarRef}
          plugins={
            isMobileView
              ? [dayGridPlugin, timeGridPlugin, interactionPlugin, listGridPlugin]
              : [dayGridPlugin, timeGridPlugin, interactionPlugin, listGridPlugin]
          }
          initialView={isMobileView ? 'listWeek' : 'dayGridMonth'}
          views={{
            timeGridThreeDay: {
              type: 'timeGrid',
              duration: { days: 3 },
              buttonText: '3 Days',
            },
            listWeek: {
              type: 'list',
              duration: { days: 7 },
              buttonText: 'List',
            },
            dayGridMonth: {
              type: 'dayGrid',
              buttonText: 'Month',
            },
            timeGridWeek: {
              type: 'timeGrid',
              buttonText: 'Week',
            },
            timeGrid1Day: {
              type: 'dayGrid',
              duration: { days: 1 },
              buttonText: 'Day',
            },
          }}
          headerToolbar={
            isMobileView
              ? false
              : {
                  left: 'prev,next',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,listWeek',
                }
          }
          titleFormat={
            isMobileView
              ? { year: 'numeric', month: 'short' }
              : { year: 'numeric', month: 'long', day: '2-digit' }
          }
          events={events}
          eventClick={onEventClick}
          height='100%'
          editable={false}
          selectable={false}
          selectMirror={true}
          dayMaxEvents={false}
          weekends={true}
          allDaySlot={false}
          eventDisplay='block'
          dayHeaderContent={(args) => {
            const weekday = args.date.toLocaleDateString('en-US', { weekday: 'short' });
            // Month/dayGrid view: weekday name only — no day number in the column header
            if (!args.view.type.startsWith('timeGrid')) {
              return <span>{weekday}</span>;
            }
            // Time-grid (week/day) views: "Mon 5" with weekday first, number second
            const day = args.date.getDate();
            return (
              <span>
                {weekday} <strong>{day}</strong>
              </span>
            );
          }}
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }}
          eventContent={(eventInfo) => <EventContent eventInfo={eventInfo} />}
          datesSet={handleDatesSet}
          customButtons={{
            prev: {
              click: handlePrev,
            },
            next: {
              click: handleNext,
            },
          }}
        />
      </div>
    </div>
  );
};

export default Calendar;
